import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import clsx from "clsx";
import { Message } from "../../../models";
import { useStore } from "../../../stores";
import styles from "./Conversation.module.scss";

interface MessageReplyPreviewProps {
  repliedOf: Message;
  isFromUser: boolean;
}

const MessageReplyPreview = ({
  repliedOf,
  isFromUser,
}: MessageReplyPreviewProps) => {
  const { selectedChat } = useStore("chats");
  const { selectedBot } = useStore("bots");
  const { users } = useStore("users");

  const {
    text,
    attachment,
    attachments,
    isFromUser: repliedIsFromUser,
    senderId,
    firstName,
    lastName,
    isDeleted,
  } = repliedOf;

  // Get user info for the replied message
  const repliedUser = useMemo(() => {
    if (repliedIsFromUser) {
      return {
        name: firstName
          ? `${firstName} ${lastName || ""}`.trim()
          : `${selectedChat?.firstName || ""} ${
              selectedChat?.lastName || ""
            }`.trim(),
      };
    }
    return users?.find((f) => f.id === senderId);
  }, [
    firstName,
    repliedIsFromUser,
    lastName,
    selectedChat?.firstName,
    selectedChat?.lastName,
    senderId,
    users,
  ]);

  // Get display name for replied message
  const repliedDisplayName = useMemo(() => {
    if (repliedIsFromUser) {
      return repliedUser?.name || "You";
    }
    return repliedUser?.name || selectedBot?.name || "Bot";
  }, [repliedIsFromUser, repliedUser?.name, selectedBot?.name]);

  // Get preview content for replied message
  const getReplyPreviewContent = () => {
    if (isDeleted) {
      return "This message was deleted";
    }

    // Handle attachments
    if (attachment) {
      switch (attachment.type) {
        case "image":
          return "📷 Photo";
        case "video":
          return "🎥 Video";
        case "audio":
          return "🎵 Audio";
        case "file":
          return "📄 Document";
        case "location":
          return "📍 Location";
        case "vcard":
          return "👤 Contact";
        case "story_mention":
          return "📖 Story";
        case "voice_call":
          return "📞 Voice call";
        case "video_call":
          return "📹 Video call";
        case "template":
          return "💬 Template";
        case "html":
          return "🌐 HTML content";
        default:
          return text || "Media";
      }
    }

    if (attachments && attachments.length > 0) {
      if (attachments.length === 1) {
        const type = attachments[0].type;
        switch (type) {
          case "image":
            return "📷 Photo";
          case "video":
            return "🎥 Video";
          case "audio":
            return "🎵 Audio";
          case "file":
            return "📄 Document";
          default:
            return text || "Media";
        }
      } else {
        return `📎 ${attachments.length} attachments`;
      }
    }

    return text || "Message";
  };

  const replyContent = getReplyPreviewContent();
  const truncatedContent =
    replyContent.length > 80
      ? replyContent.substring(0, 80) + "..."
      : replyContent;

  const scrollToMessage = (messageId: number) => {
    const messageElement = document.querySelector(`#chat-message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth" });

      // Highlight the message temporarily
      messageElement.classList.add(styles.highlightedMessage);
      setTimeout(() => {
        messageElement.classList.remove(styles.highlightedMessage);
      }, 2000);
    }
  };

  return (
    <div
      className={clsx("mb-2 px-3 py-2 is-relative", {
        "has-background-white-ter": isFromUser,
        "has-background-light": !isFromUser,
      })}
      onClick={() => scrollToMessage(repliedOf.id)}
      style={{
        borderLeft: `3px solid ${repliedIsFromUser ? "#2a79ef" : "#667781"}`,
        borderRadius: "8px",
        backgroundColor: isFromUser
          ? "rgba(0, 0, 0, 0.05)"
          : "rgba(255, 255, 255, 0.1)",
        cursor: "pointer",
      }}
    >
      {/* Replied to indicator line */}
      <div
        className="is-size-7 has-text-weight-semibold mb-1"
        style={{
          color: repliedIsFromUser ? "#2a79ef" : "#667781",
          fontSize: "0.75rem",
        }}
      >
        {repliedDisplayName}
      </div>

      {/* Replied message content */}
      <div
        className="is-size-7"
        style={{
          color: isFromUser ? "#4a5568" : "rgba(3, 3, 3, 0.8)",
          fontSize: "0.8rem",
          lineHeight: "1.2",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {truncatedContent}
      </div>
    </div>
  );
};

export default observer(MessageReplyPreview);
